import { useEffect, useState } from 'react';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

let loaderPromise = null;

/**
 * Load the Google Identity Services (GIS) client script once and share it.
 * Resolves with `window.google.accounts.id`.
 */
export const loadGoogleIdentity = () => {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.accounts?.id) resolve(window.google.accounts.id);
      else reject(new Error('Google Identity Services failed to initialise.'));
    };
    script.onerror = () => {
      loaderPromise = null; // allow retry
      reject(new Error('Could not load Google sign-in. Check your connection or ad blocker.'));
    };
    document.head.appendChild(script);
  });
  return loaderPromise;
};

/** React hook wrapper around loadGoogleIdentity(). */
export const useGoogleIdentity = () => {
  const [state, setState] = useState({ ready: !!window.google?.accounts?.id, error: null });

  useEffect(() => {
    let cancelled = false;
    loadGoogleIdentity()
      .then(() => !cancelled && setState({ ready: true, error: null }))
      .catch((err) => !cancelled && setState({ ready: false, error: err.message }));
    return () => { cancelled = true; };
  }, []);

  return state;
};
